import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  useColorModeValue,
  IconButton,
  HStack,
  Flex,
  Button,
  VStack,
  Badge,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { ChatIcon, DeleteIcon, AddIcon, ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { usePopulatedLeague } from '../hooks/usePopulatedLeague';
import { useMyLeagueStore } from '../store/myLeagueStore';
import apiClient from '../lib/api/apiClient';

interface JoinRequest {
  id: number;
  team_id: number;
  league_id: number;
  user_id: number;
  user: {
    display_name: string;
    email: string;
  }
  requested_roles: string[];
  message: string;
  status: 'pending' | 'approved' | 'declined';
  created_at: string;
}

interface Invite {
  id: number;
  team_id: number;
  league_id: number;
  user_id: number;
  invited_email: string;
  requested_roles: string[];
  status: 'pending' | 'accepted' | 'declined';
  decline_reason?: string;
  created_at: string;
}

type SortField = 'name' | 'email' | 'roles' | 'status' | 'created_at';
type SortDirection = 'asc' | 'desc';
type InviteSortField = 'email' | 'roles' | 'status' | 'created_at';

interface TeamOwnershipProps {
  teamId: number | string;
  leagueId: number | string;
  onInviteOpen?: () => void;
  onViewRequest?: (request: JoinRequest) => void;
  refreshTrigger?: number; // Increment this to trigger refresh
  isMyTeam?: boolean; // Whether this is the user's team
}

export const TeamOwnership: React.FC<TeamOwnershipProps> = ({ teamId, leagueId, onInviteOpen, onViewRequest, refreshTrigger, isMyTeam = false }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');

  const [joinRequests, setJoinRequests] = useState<JoinRequest[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [inviteSortField, setInviteSortField] = useState<InviteSortField>('created_at');
  const [inviteSortDirection, setInviteSortDirection] = useState<SortDirection>('desc');
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Use myLeague - it contains all teams in the league with their owners
  const myLeague = useMyLeagueStore(state => state.myLeague);

  // Find the team in the league structure - this is the same object that has salary_cap
  let teamWithOwners: any = null;
  if (myLeague?.conferences) {
    for (const conf of myLeague.conferences) {
      if (conf.divisions) {
        for (const div of conf.divisions) {
          if (div.teams) {
            const team = div.teams.find((t: any) => String(t.id) === String(teamId));
            if (team) {
              teamWithOwners = team;
              break;
            }
          }
        }
        if (teamWithOwners) break;
      }
    }
  }

  const fetchJoinRequests = React.useCallback(async () => {
    if (!teamId || !leagueId) return;
    try {
      setFetchError(null);
      const response = await apiClient.get(`/team/join-requests/${teamId}/${leagueId}`);
      setJoinRequests(response.data || []);
    } catch (err: any) {
      console.error('Error fetching join requests:', err);
      setFetchError(err?.response?.data?.message || 'Failed to load join requests');
    }
  }, [teamId, leagueId]);

  const fetchInvites = React.useCallback(async () => {
    if (!teamId || !leagueId) return;
    try {
      const response = await apiClient.get(`/team/invites/${teamId}/${leagueId}`);
      setInvites(response.data || []);
    } catch (err: any) {
      console.error('Error fetching invites:', err);
      // Don't set error state for invites as it's not critical
    }
  }, [teamId, leagueId]);

  useEffect(() => {
    if (teamId && leagueId && isMyTeam) {
      fetchJoinRequests();
      fetchInvites();
    }
  }, [teamId, leagueId, refreshTrigger, fetchJoinRequests, fetchInvites, isMyTeam]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleInviteSort = (field: InviteSortField) => {
    if (inviteSortField === field) {
      setInviteSortDirection(inviteSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setInviteSortField(field);
      setInviteSortDirection('asc');
    }
  };

  const getSortedRequests = () => {
    return [...joinRequests].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.user.display_name.toLowerCase();
          bValue = b.user.display_name.toLowerCase();
          break;
        case 'email':
          aValue = a.user.email.toLowerCase();
          bValue = b.user.email.toLowerCase();
          break;
        case 'roles':
          aValue = a.requested_roles.length;
          bValue = b.requested_roles.length;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortedInvites = () => {
    return [...invites].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (inviteSortField) {
        case 'email':
          aValue = a.invited_email.toLowerCase();
          bValue = b.invited_email.toLowerCase();
          break;
        case 'roles':
          aValue = a.requested_roles.length;
          bValue = b.requested_roles.length;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return inviteSortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return inviteSortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { colorScheme: 'yellow', label: 'Pending' },
      accepted: { colorScheme: 'green', label: 'Approved' },
      declined: { colorScheme: 'red', label: 'Declined' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <Badge colorScheme={config.colorScheme} fontSize="sm">
        {config.label}
      </Badge>
    );
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field;
    return (
      <Th
        cursor="pointer"
        userSelect="none"
        onClick={() => handleSort(field)}
        _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
        bg={'transparent'}
        transition="background-color 0.2s"
      >
        <HStack spacing={1}>
          <Text>{children}</Text>
          {isActive && (
            <Icon
              as={sortDirection === 'asc' ? ChevronUpIcon : ChevronDownIcon}
              w={3}
              h={3}
              color="green.500"
            />
          )}
        </HStack>
      </Th>
    );
  };

  const InviteSortableHeader = ({ field, children }: { field: InviteSortField; children: React.ReactNode }) => {
    const isActive = inviteSortField === field;
    return (
      <Th
        cursor="pointer"
        userSelect="none"
        onClick={() => handleInviteSort(field)}
        _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
        bg={'transparent'}
        transition="background-color 0.2s"
      >
        <HStack spacing={1}>
          <Text>{children}</Text>
          {isActive && (
            <Icon
              as={inviteSortDirection === 'asc' ? ChevronUpIcon : ChevronDownIcon}
              w={3}
              h={3}
              color="green.500"
            />
          )}
        </HStack>
      </Th>
    );
  };

  // Get owners from the team object (same object that has salary_cap)
  const teamOwners: Array<{
    displayName: string;
    role: string | null;
    roleId: number | null;
    ownership: number;
  }> = teamWithOwners?.owners ? teamWithOwners.owners.map((owner: any) => ({
    displayName: owner.displayName || owner.display_name || 'Unknown',
    role: owner.role || null,
    roleId: owner.roleId || owner.role_value || null,
    ownership: owner.ownership || 0
  })) : [];

  return (
    <Card bg={bgColor} borderColor={borderColor} borderWidth="1px">
      <CardHeader>
        <Flex justify="space-between" align="center">
          <Heading size="md">Team Ownership</Heading>
          {isMyTeam && onInviteOpen && (
            <Button
              leftIcon={<AddIcon />}
              colorScheme="green"
              onClick={onInviteOpen}
              size="sm"
            >
              Invite User
            </Button>
          )}
        </Flex>
      </CardHeader>
      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* Team Ownership Table */}
          {teamOwners.length === 0 ? (
            <Text color={secondaryTextColor} fontStyle="italic">
              No owners have been assigned to this team.
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Role</Th>
                    <Th isNumeric>Ownership %</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {teamOwners.map((owner, idx) => (
                    <Tr key={idx}>
                      <Td>
                        <Text fontWeight="medium" color={textColor}>
                          {owner.displayName || 'Unknown'}
                        </Text>
                      </Td>
                      <Td>
                        <Text color={secondaryTextColor}>{owner.role || 'N/A'}</Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontWeight="medium" color={textColor}>
                          {owner.ownership ? `${parseFloat(owner.ownership.toString()).toFixed(1)}%` : '0%'}
                        </Text>
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <IconButton
                            aria-label="Message"
                            icon={<ChatIcon />}
                            size="xs"
                            variant="ghost"
                            colorScheme="teal"
                          />
                          {isMyTeam && (
                            <IconButton
                              aria-label="Delete"
                              icon={<DeleteIcon />}
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                            />
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {isMyTeam && (
            <>
              <Divider />

              {/* Invited Section */}
              <Box>
            <Heading size="md" mb={4} textAlign="left">
              Invited
            </Heading>
            {invites.length === 0 ? (
              <VStack spacing={3} py={4}>
                <Text fontSize="lg" color={secondaryTextColor}>
                  No team invites at this time.
                </Text>
                <Text fontSize="sm" color={secondaryTextColor}>
                  Invites you send to users will appear here.
                </Text>
              </VStack>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <InviteSortableHeader field="email">Email</InviteSortableHeader>
                      <InviteSortableHeader field="roles">Roles</InviteSortableHeader>
                      <InviteSortableHeader field="status">Status</InviteSortableHeader>
                      <Th>Message</Th>
                      <InviteSortableHeader field="created_at">Invited</InviteSortableHeader>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {getSortedInvites().map((invite) => (
                      <Tr key={invite.id}>
                        <Td>
                          <Text color={secondaryTextColor} fontSize="sm">
                            {invite.invited_email}
                          </Text>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            {invite.requested_roles.slice(0, 2).map((role, idx) => (
                              <Text key={idx} fontSize="sm" color={textColor}>
                                {role}
                              </Text>
                            ))}
                            {invite.requested_roles.length > 2 && (
                              <Text fontSize="xs" color={secondaryTextColor}>
                                +{invite.requested_roles.length - 2} more
                              </Text>
                            )}
                          </VStack>
                        </Td>
                        <Td>{getStatusBadge(invite.status)}</Td>
                        <Td>
                          {invite.status === 'declined' && invite.decline_reason ? (
                            <Text 
                              fontSize="xs" 
                              color={secondaryTextColor}
                              maxW="200px"
                              noOfLines={2}
                              title={invite.decline_reason}
                            >
                              {invite.decline_reason}
                            </Text>
                          ) : (
                            <Text fontSize="xs" color="gray.500">
                              -
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <Text fontSize="sm" color={secondaryTextColor}>
                            {new Date(invite.created_at).toLocaleDateString()}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Requested to Join Section */}
          <Box>
            <Heading size="md" mb={4} textAlign="left">
              Requested to Join
            </Heading>
            {fetchError ? (
              <VStack spacing={3} py={4}>
                <Text fontSize="lg" color="red.500">
                  {fetchError}
                </Text>
                <Button
                  size="sm"
                  colorScheme="green"
                  onClick={() => {
                    setFetchError(null);
                    fetchJoinRequests();
                  }}
                >
                  Try Again
                </Button>
              </VStack>
            ) : joinRequests.length === 0 ? (
              <VStack spacing={3} py={4}>
                <Text fontSize="lg" color={secondaryTextColor}>
                  No join requests at this time.
                </Text>
                <Text fontSize="sm" color={secondaryTextColor}>
                  When users request to join your team, they will appear here.
                </Text>
              </VStack>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <SortableHeader field="name">Name</SortableHeader>
                      <SortableHeader field="email">Email</SortableHeader>
                      <SortableHeader field="roles">Roles</SortableHeader>
                      <SortableHeader field="status">Status</SortableHeader>
                      <SortableHeader field="created_at">Requested</SortableHeader>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {getSortedRequests().map((request) => (
                      <Tr key={request.id}>
                        <Td>
                          <Text fontWeight="medium" color={textColor}>
                            {request.user.display_name}
                          </Text>
                        </Td>
                        <Td>
                          <Text color={secondaryTextColor} fontSize="sm">
                            {request.user.email}
                          </Text>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            {request.requested_roles.slice(0, 2).map((role, idx) => (
                              <Text key={idx} fontSize="sm" color={textColor}>
                                {role}
                              </Text>
                            ))}
                            {request.requested_roles.length > 2 && (
                              <Text fontSize="xs" color={secondaryTextColor}>
                                +{request.requested_roles.length - 2} more
                              </Text>
                            )}
                          </VStack>
                        </Td>
                        <Td>{getStatusBadge(request.status)}</Td>
                        <Td>
                          <Text fontSize="sm" color={secondaryTextColor}>
                            {new Date(request.created_at).toLocaleDateString()}
                          </Text>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="blue"
                              onClick={() => onViewRequest && onViewRequest(request)}
                            >
                              View
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default TeamOwnership;

